<template>
  <div
    v-if="!authUIFlags.isFetching"
    id="app"
    class="app-wrapper app-root"
    :class="{ 'app-rtl--wrapper': isRTLView }"
  >
    <link
      rel="stylesheet"
      href="https://cdn-uicons.flaticon.com/uicons-solid-rounded/css/uicons-solid-rounded.css"
    />
    <link
      rel="stylesheet"
      href="https://cdn-uicons.flaticon.com/uicons-regular-straight/css/uicons-regular-straight.css"
    />
    <link
      href="https://cdn.jsdelivr.net/npm/@flaticon/flaticon-uicons@3.3.1/css/all/all.min.css"
      rel="stylesheet"
    />
    <transition name="fade" mode="out-in">
      <router-view />
    </transition>
    <woot-snackbar-box />
    <network-notification />
  </div>
  <loading-state v-else />
</template>

<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification';
import vueActionCable from './helper/actionCable';
import WootSnackbarBox from './components/SnackbarContainer';
import rtlMixin from 'shared/mixins/rtlMixin';
import uiSettingsMixin from './mixins/uiSettings';
import {
  applyThemePreference,
  watchSystemTheme,
  getStoredThemePreference,
} from 'shared/helpers/themeHelper';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import { loadFacebookSdk } from './helper/facebookSdk';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    WootSnackbarBox,
  },

  mixins: [rtlMixin, uiSettingsMixin],

  data() {
    return {
      latestChatwootVersion: null,
      unwatchSystemTheme: null,
    };
  },

  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      authUIFlags: 'getAuthUIFlags',
      currentAccountId: 'getCurrentAccountId',
      getUnreadConversationsCount: 'conversationStats/getUnreadCount',
    }),
    hasAccounts() {
      const { accounts = [] } = this.currentUser || {};
      return accounts.length > 0;
    },
    themePreference() {
      // Logado: fonte de verdade é ui_settings.color_scheme. Deslogado (tela de
      // login), ui_settings está vazio — cai no cache do localStorage (última
      // escolha do usuário) em vez de forçar 'auto'/SO, para o login seguir o
      // tema selecionado. US-4328
      return this.uiSettings.color_scheme || getStoredThemePreference();
    },
  },

  watch: {
    themePreference() {
      this.applyTheme();
    },
    currentUser() {
      if (this.currentUser) {
        (function(d, s, id, user) {
          var js;
          var fjs = d.getElementsByTagName(s)[0];

          if (d.getElementById(id)) return;

          if (!user.account_id) {
            console.warn(
              'Dados do usuário não disponíveis. Certifique-se de definir window.userData antes de carregar o script.'
            );
            return;
          }

          window.usetifulTags = {
            userId: user.account_id,
            firstName: user.name,
            userRole: user.type_role,
          };

          js = d.createElement(s);
          js.id = id;
          js.src = 'https://www.usetiful.com/dist/usetiful.js';
          js.dataset.token = 'b600d7afa64cfd3670f29f308d97303f';
          fjs.parentNode.insertBefore(js, fjs);
        })(document, 'script', 'usetifulScript', this.currentUser);
      }
    },
    currentAccountId() {
      if (this.currentAccountId) {
        this.initializeAccount();
      }
    },
    getUnreadConversationsCount() {
      if (this.getUnreadConversationsCount) {
        document.title = `(${this.getUnreadConversationsCount}) Omnidesk`;
      } else {
        document.title = `Omnidesk`;
      }
    },
  },
  mounted() {
    this.applyTheme();
    this.setLocale(window.chatwootConfig.selectedLocale);
    this.$store.dispatch('conversationStats/setCountUnreadConversations');
    // Preload Meta SDK without binding an appId. WhatsApp (wbAppId) and
    // Messenger/IG (fbAppId) each call ensureFacebookSdk before FB.login.
    loadFacebookSdk().catch(() => {});
  },
  beforeDestroy() {
    if (this.unwatchSystemTheme) this.unwatchSystemTheme();
  },
  methods: {
    // Tema (US-4328): aplica a preferência (light|dark|auto) no <html> e,
    // no modo auto, observa a troca de tema do SO em tempo real.
    applyTheme() {
      const pref = this.themePreference;
      applyThemePreference(pref);
      if (this.unwatchSystemTheme) {
        this.unwatchSystemTheme();
        this.unwatchSystemTheme = null;
      }
      if (pref === 'auto') {
        this.unwatchSystemTheme = watchSystemTheme(() =>
          applyThemePreference('auto')
        );
      }
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const {
        locale,
        latest_chatwoot_version: latestChatwootVersion,
      } = this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      this.setLocale(locale);
      this.updateRTLDirectionView(locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(pubsubToken);

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<style lang="scss">
@import './assets/scss/app';
</style>

<style>
.mt-50 {
  margin-top: 55px !important;
}
.bg-primary,
.button.success,
.nice {
  background-color: var(--g-20);
  color: var(--s-50);
}

.button[disabled].success,
.bg-primary[disabled] {
  background-color: var(--g-20) !important;
  color: var(--s-50) !important;
  opacity: 0.4 !important;
}

.button.success:hover,
.button.success:focus {
  background-color: var(--g-30) !important;
  color: var(--s-50) !important;
}

.btn-primary-create,
.btn-primary,
.clear,
.sub-block-title {
  color: var(--g-20) !important ;
}

input:focus,
textarea:focus {
  outline: none !important;
  border-color: var(--g-20) !important;
  box-shadow: 0 0 3px var(--g-20) !important;
}

.modal--close,
.button--only-icon {
  color: var(--s-700) !important;
}

.secondary {
  color: var(--s-700) !important;
}

.pac-container {
  z-index: 9999 !important;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
